import { Injectable, EventEmitter } from '@angular/core';
import { Vector3 } from 'three';
import { SnapResult, SnapConfig } from '../types';

/**
 * Snap Engine Service
 *
 * Handles snapping to grid, CAD edges/vertices, sketches,
 * and geometric constraints (horizontal/vertical).
 */
@Injectable({
  providedIn: 'root'
})
export class SnapEngineService {
  private config: SnapConfig = {
    enabled: true,
    gridSize: 5,
    edgeSnapDistance: 4,
    vertexSnapDistance: 2,
    enableHorizontal: true,
    enableVertical: true
  };

  // Cached vertices from CAD shapes (for vertex snapping)
  private cadVertices: { point: Vector3; shapeId: string }[] = [];

  // Cached edge segments from CAD shapes (for edge snapping)
  private cadEdgeSegments: { p1: Vector3; p2: Vector3; shapeId: string }[] = [];
  
  // Cached edge midpoints from CAD shapes
  private cadMidpoints: { point: Vector3; shapeId: string }[] = [];
  
  // Cached shape centers (bounding box centers)
  private shapeCenters: { point: Vector3; shapeId: string }[] = [];
  
  // Cached circle centers from CAD shape edges
  private cadCircleCenters: { point: Vector3; shapeId: string; radius: number; label: string }[] = [];
  
  // Sketch entity snap points
  private sketchVertices: { point: Vector3; entityId: string; label: string }[] = [];
  private sketchMidpoints: { point: Vector3; entityId: string; label: string }[] = [];
  private sketchCenters: { point: Vector3; entityId: string; label: string }[] = [];
  private sketchEdges: { p1: Vector3; p2: Vector3; entityId: string }[] = [];
  
  // Last known constraint reference point (for H/V constraints)
  private lastPoint: Vector3 | null = null;
  
  // Current sketch plane for correct distance calculations
  private currentSketchPlane: 'XY' | 'XZ' | 'YZ' | 'custom' = 'XY';
  private customPlaneNormal: Vector3 = new Vector3(0, 0, 1);
  private customPlaneOrigin: Vector3 = new Vector3(0, 0, 0);
  private customPlaneXAxis: Vector3 = new Vector3(1, 0, 0);
  private customPlaneYAxis: Vector3 = new Vector3(0, 1, 0);
  
  // Events
  public onSnapFound = new EventEmitter<SnapResult | null>();
  public onConfigChanged = new EventEmitter<SnapConfig>();

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get current snap configuration
   */
  getConfig(): SnapConfig {
    return { ...this.config };
  }

  /**
   * Update snap configuration
   */
  setConfig(config: Partial<SnapConfig>): void {
    this.config = { ...this.config, ...config };
    this.onConfigChanged.emit(this.config);
  }

  /**
   * Enable/disable snapping
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    this.onConfigChanged.emit(this.config);
  }

  /**
   * Set the current sketch plane for correct distance calculations
   */
  setSketchPlane(
    plane: 'XY' | 'XZ' | 'YZ' | 'custom',
    customNormal?: [number, number, number],
    customOrigin?: [number, number, number],
    customXAxis?: [number, number, number],
    customYAxis?: [number, number, number]
  ): void {
    this.currentSketchPlane = plane;
    if (plane === 'custom' && customNormal) {
      this.customPlaneNormal = new Vector3(customNormal[0], customNormal[1], customNormal[2]).normalize();
      if (customOrigin) {
        this.customPlaneOrigin = new Vector3(customOrigin[0], customOrigin[1], customOrigin[2]);
      }

      if (customXAxis) {
        const xAxis = new Vector3(customXAxis[0], customXAxis[1], customXAxis[2]).normalize();
        const yAxis = customYAxis
          ? new Vector3(customYAxis[0], customYAxis[1], customYAxis[2]).normalize()
          : new Vector3().crossVectors(this.customPlaneNormal, xAxis).normalize();
        this.customPlaneXAxis = xAxis;
        this.customPlaneYAxis = yAxis;
      } else {
        let xAxis = new Vector3(1, 0, 0);
        if (Math.abs(this.customPlaneNormal.dot(xAxis)) > 0.9) {
          xAxis = new Vector3(0, 1, 0);
        }
        const yAxis = new Vector3().crossVectors(this.customPlaneNormal, xAxis).normalize();
        xAxis = new Vector3().crossVectors(yAxis, this.customPlaneNormal).normalize();
        this.customPlaneXAxis = xAxis;
        this.customPlaneYAxis = yAxis;
      }
    }
    
  }

  /**
   * Get current sketch plane
   */
  getSketchPlane(): 'XY' | 'XZ' | 'YZ' | 'custom' {
    return this.currentSketchPlane;
  }

  /**
   * Deduplicate circle centers - when multiple circles share the same center,
   * keep all unique radii (concentric circles) but remove duplicates
   */
  private deduplicateCircleCenters<T extends { point: Vector3; radius: number; label: string }>(circles: T[]): T[] {
    if (circles.length <= 1) return circles;
    
    // First, round radii to 0.1mm precision for consistency
    for (const circle of circles) {
      circle.radius = Math.round(circle.radius * 10) / 10;
      circle.label = `Circle Center (R=${circle.radius.toFixed(1)})`;
    }
    
    const result: T[] = [];
    const centerThreshold = 1.0; // Centers within 1mm are considered the same
    const radiusThreshold = 0.5; // Radii within 0.5mm are considered the same
    
    for (const circle of circles) {
      // Check if we already have a circle at this center with similar radius
      const existingIdx = result.findIndex(existing => {
        const centerDist = existing.point.distanceTo(circle.point);
        const radiusDiff = Math.abs(existing.radius - circle.radius);
        return centerDist < centerThreshold && radiusDiff < radiusThreshold;
      });
      
      if (existingIdx === -1) {
        // New unique circle
        result.push(circle);
      } else {
        // Duplicate - keep the one with larger radius (usually more reliable)
        if (circle.radius > result[existingIdx].radius) {
          result[existingIdx] = circle;
        }
      }
    }
    
    return result;
  }

  /**
   * Split a polyline into separate segments at "jump" points
   * where consecutive points are unusually far apart
   */
  private splitWireIntoSegments(points: Vector3[]): Vector3[][] {
    if (points.length < 3) return [points];
    
    // Calculate average distance between consecutive points
    let totalDist = 0;
    const distances: number[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      const d = points[i].distanceTo(points[i + 1]);
      distances.push(d);
      totalDist += d;
    }
    const avgDist = totalDist / distances.length;
    
    // Threshold for detecting a "jump" - 5x average distance
    const jumpThreshold = avgDist * 5;
    
    // Split at jump points
    const segments: Vector3[][] = [];
    let currentSegment: Vector3[] = [points[0]];
    
    for (let i = 0; i < distances.length; i++) {
      if (distances[i] > jumpThreshold && currentSegment.length >= 3) {
        // Close the current segment if it loops back
        const firstPt = currentSegment[0];
        const lastPt = currentSegment[currentSegment.length - 1];
        if (firstPt.distanceTo(lastPt) < avgDist * 2) {
          // It's a closed loop, good for circle detection
          segments.push(currentSegment);
        } else if (currentSegment.length >= 8) {
          // Large open segment, might still be useful
          segments.push(currentSegment);
        }
        currentSegment = [];
      }
      currentSegment.push(points[i + 1]);
    }
    
    // Don't forget the last segment
    if (currentSegment.length >= 3) {
      const firstPt = currentSegment[0];
      const lastPt = currentSegment[currentSegment.length - 1];
      if (firstPt.distanceTo(lastPt) < avgDist * 2 || currentSegment.length >= 8) {
        segments.push(currentSegment);
      }
    }
    
    // If no segments were created (no jumps), use the original points
    if (segments.length === 0) {
      segments.push(points);
    }
    
    return segments;
  }

  /**
   * Fit a circle to a set of points (world coordinates)
   */
  fitCircleFromPoints(points: Vector3[]): { center: Vector3; radius: number } | null {
    return this.detectCircle(points);
  }

  /**
   * Detect if points form a circle or arc
   * Uses multiple detection strategies for robustness
   * Returns center and radius if detected, null otherwise
   */
  private detectCircle(points: Vector3[]): { center: Vector3; radius: number } | null {
    if (points.length < 3) return null;
    
    // Remove duplicate points
    const uniquePoints = this.deduplicatePoints(points, 0.05);
    if (uniquePoints.length < 3) return null;
    
    // Calculate the approximate perimeter to determine loop closure threshold
    let perimeter = 0;
    for (let i = 0; i < uniquePoints.length - 1; i++) {
      perimeter += uniquePoints[i].distanceTo(uniquePoints[i + 1]);
    }
    const avgSegmentLength = perimeter / (uniquePoints.length - 1);
    
    // Check if this is a closed loop - use adaptive threshold based on segment length
    const closureThreshold = Math.max(avgSegmentLength * 3, 1.0);
    const gapToClose = uniquePoints[0].distanceTo(uniquePoints[uniquePoints.length - 1]);
    const isClosedLoop = uniquePoints.length > 3 && gapToClose < closureThreshold;
    
    // Try detection on closed loops first
    if (isClosedLoop) {
      const result = this.detectSingleCircle(uniquePoints);
      if (result) {
        return result;
      }
    }
    
    // For non-closed or failed closed detection, try RANSAC-style detection
    // This works better for partial circles or noisy data
    const ransacResult = this.detectCircleRANSAC(uniquePoints);
    if (ransacResult) {
      return ransacResult;
    }
    
    return null;
  }
  
  /**
   * RANSAC-style circle detection - robust to outliers and partial circles
   */
  private detectCircleRANSAC(points: Vector3[]): { center: Vector3; radius: number } | null {
    if (points.length < 4) return null;
    
    // Get the best-fit plane for the points
    const normal = this.getPlaneNormal(points);
    if (!normal) return null;
    
    // Create local 2D coordinate system
    const centroid3D = new Vector3();
    for (const p of points) centroid3D.add(p);
    centroid3D.divideScalar(points.length);
    
    const up = new Vector3(0, 1, 0);
    if (Math.abs(normal.dot(up)) > 0.99) up.set(1, 0, 0);
    const xAxis = new Vector3().crossVectors(up, normal).normalize();
    const yAxis = new Vector3().crossVectors(normal, xAxis).normalize();
    
    // Project to 2D
    const points2D = points.map(p => {
      const v = p.clone().sub(centroid3D);
      return { x: v.dot(xAxis), y: v.dot(yAxis) };
    });
    
    let bestCircle: { cx: number; cy: number; r: number; inliers: number } | null = null;
    const iterations = Math.min(points2D.length * 2, 50);
    
    for (let iter = 0; iter < iterations; iter++) {
      // Pick 3 random points
      const indices = this.pickRandomIndices(points2D.length, 3);
      const p1 = points2D[indices[0]];
      const p2 = points2D[indices[1]];
      const p3 = points2D[indices[2]];
      
      // Fit circle through 3 points
      const circle = this.fitCircleThrough3Points2D(p1, p2, p3);
      if (!circle) continue;
      
      // Count inliers (points within 15% of radius from circle)
      const tolerance = Math.max(circle.r * 0.15, 0.5);
      let inliers = 0;
      for (const pt of points2D) {
        const dist = Math.sqrt((pt.x - circle.cx) ** 2 + (pt.y - circle.cy) ** 2);
        if (Math.abs(dist - circle.r) < tolerance) {
          inliers++;
        }
      }
      
      if (!bestCircle || inliers > bestCircle.inliers) {
        bestCircle = { ...circle, inliers };
      }
    }
    
    // Require at least 60% inliers
    if (!bestCircle || bestCircle.inliers < points2D.length * 0.6) {
      return null;
    }
    
    // Refine with least squares using inliers only
    const tolerance = Math.max(bestCircle.r * 0.15, 0.5);
    const inlierPoints = points2D.filter(pt => {
      const dist = Math.sqrt((pt.x - bestCircle!.cx) ** 2 + (pt.y - bestCircle!.cy) ** 2);
      return Math.abs(dist - bestCircle!.r) < tolerance;
    });
    
    const refined = this.refinedCircleFit2D(inlierPoints);
    if (!refined) return null;
    
    // Convert back to 3D
    const center3D = centroid3D.clone()
      .add(xAxis.clone().multiplyScalar(refined.cx))
      .add(yAxis.clone().multiplyScalar(refined.cy));
    
    return { center: center3D, radius: refined.r };
  }
  
  /**
   * Pick n random unique indices from 0 to max-1
   */
  private pickRandomIndices(max: number, n: number): number[] {
    const indices: number[] = [];
    while (indices.length < n) {
      const idx = Math.floor(Math.random() * max);
      if (!indices.includes(idx)) {
        indices.push(idx);
      }
    }
    return indices;
  }
  
  /**
   * Fit a circle through exactly 3 points in 2D
   */
  private fitCircleThrough3Points2D(p1: {x: number; y: number}, p2: {x: number; y: number}, p3: {x: number; y: number}): { cx: number; cy: number; r: number } | null {
    const ax = p1.x, ay = p1.y;
    const bx = p2.x, by = p2.y;
    const cx = p3.x, cy = p3.y;
    
    const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
    if (Math.abs(d) < 0.0001) return null; // Collinear
    
    const ux = ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / d;
    const uy = ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / d;
    const r = Math.sqrt((ax - ux) ** 2 + (ay - uy) ** 2);
    
    if (r < 0.05 || r > 50000) return null;
    
    return { cx: ux, cy: uy, r };
  }
  
  /**
   * Refine circle fit using least squares on 2D points
   */
  private refinedCircleFit2D(points: {x: number; y: number}[]): { cx: number; cy: number; r: number } | null {
    if (points.length < 3) return null;
    
    // Simple algebraic fit
    let sumX = 0, sumY = 0, sumXX = 0, sumYY = 0, sumXY = 0;
    let sumXXX = 0, sumYYY = 0, sumXXY = 0, sumXYY = 0;
    
    for (const p of points) {
      const xx = p.x * p.x;
      const yy = p.y * p.y;
      sumX += p.x;
      sumY += p.y;
      sumXX += xx;
      sumYY += yy;
      sumXY += p.x * p.y;
      sumXXX += p.x * xx;
      sumYYY += p.y * yy;
      sumXXY += xx * p.y;
      sumXYY += p.x * yy;
    }
    
    const n = points.length;
    const A = n * sumXX - sumX * sumX;
    const B = n * sumXY - sumX * sumY;
    const C = n * sumYY - sumY * sumY;
    const D = 0.5 * (n * sumXXX + n * sumXYY - sumX * sumXX - sumX * sumYY);
    const E = 0.5 * (n * sumXXY + n * sumYYY - sumY * sumXX - sumY * sumYY);
    
    const denom = A * C - B * B;
    if (Math.abs(denom) < 0.0001) return null;
    
    const cx = (D * C - B * E) / denom;
    const cy = (A * E - B * D) / denom;
    
    let sumDist = 0;
    for (const p of points) {
      sumDist += Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2);
    }
    const r = sumDist / n;
    
    if (r < 0.05 || r > 50000) return null;
    
    return { cx, cy, r };
  }
  
  /**
   * Detect a single circle from points that form a closed loop
   * This assumes ALL points belong to ONE circle
   */
  private detectSingleCircle(points: Vector3[]): { center: Vector3; radius: number } | null {
    if (points.length < 4) return null;
    
    // Calculate centroid
    const centroid = new Vector3();
    for (const p of points) centroid.add(p);
    centroid.divideScalar(points.length);
    
    // Calculate distances from centroid
    const distances = points.map(p => p.distanceTo(centroid));
    const avgDistance = distances.reduce((a, b) => a + b, 0) / distances.length;
    
    if (avgDistance < 0.01) return null; // Too small
    
    // Calculate standard deviation
    const variance = distances.reduce((sum, d) => sum + (d - avgDistance) ** 2, 0) / distances.length;
    const stdDev = Math.sqrt(variance);
    
    // For a true circle, standard deviation should be very small relative to radius
    // Allow 15% deviation for tessellated circles (increased from 10%)
    const relativeStdDev = stdDev / avgDistance;
    
    if (relativeStdDev < 0.15) {
      // This is a good circle
      return { center: centroid, radius: avgDistance };
    }
    
    // If standard deviation is too high, this might be multiple circles or not a circle
    // Try least squares as fallback
    return this.detectCircleByLeastSquaresFinal(points);
  }
  
  /**
   * Final attempt at circle detection using algebraic least squares
   */
  private detectCircleByLeastSquaresFinal(points: Vector3[]): { center: Vector3; radius: number } | null {
    if (points.length < 4) return null;
    
    // Get the best-fit plane for the points
    const normal = this.getPlaneNormal(points);
    if (!normal) return null;
    
    // Create local 2D coordinate system
    const centroid = new Vector3();
    for (const p of points) centroid.add(p);
    centroid.divideScalar(points.length);
    
    const up = new Vector3(0, 1, 0);
    if (Math.abs(normal.dot(up)) > 0.99) up.set(1, 0, 0);
    const xAxis = new Vector3().crossVectors(up, normal).normalize();
    const yAxis = new Vector3().crossVectors(normal, xAxis).normalize();
    
    // Project all points to 2D
    const points2D = points.map(p => {
      const v = p.clone().sub(centroid);
      return { x: v.dot(xAxis), y: v.dot(yAxis) };
    });
    
    // Algebraic circle fit — shared with the RANSAC refinement path.
    const fit = this.refinedCircleFit2D(points2D);
    if (!fit) return null;
    const { cx: centerX, cy: centerY, r: radius } = fit;

    // Calculate standard deviation
    let sumSqDiff = 0;
    for (const p of points2D) {
      const dist = Math.sqrt((p.x - centerX) ** 2 + (p.y - centerY) ** 2);
      sumSqDiff += (dist - radius) ** 2;
    }
    const stdDev = Math.sqrt(sumSqDiff / points2D.length);
    const relativeError = stdDev / radius;
    
    // Accept if error is less than 15%
    if (relativeError < 0.15) {
      const center3D = centroid.clone()
        .add(xAxis.clone().multiplyScalar(centerX))
        .add(yAxis.clone().multiplyScalar(centerY));
      return { center: center3D, radius };
    }
    
    return null;
  }
  
  /**
   * Remove duplicate points that are very close together
   */
  private deduplicatePoints(points: Vector3[], threshold: number): Vector3[] {
    const unique: Vector3[] = [];
    for (const p of points) {
      const isDupe = unique.some(u => u.distanceTo(p) < threshold);
      if (!isDupe) {
        unique.push(p.clone());
      }
    }
    return unique;
  }
  
  /**
   * Get the normal of a plane defined by points
   */
  private getPlaneNormal(points: Vector3[]): Vector3 | null {
    if (points.length < 3) return null;
    
    // Use first 3 non-collinear points
    const p0 = points[0];
    const p1 = points[Math.floor(points.length / 3)];
    const p2 = points[Math.floor(points.length * 2 / 3)];
    
    const v1 = p1.clone().sub(p0);
    const v2 = p2.clone().sub(p0);
    
    const normal = v1.cross(v2);
    if (normal.length() < 0.001) return null;
    
    return normal.normalize();
  }

  /**
   * Get snap data statistics for debugging
   */
  getSnapStats(): { vertices: number; midpoints: number; centers: number; edges: number; plane: string } {
    return {
      vertices: this.cadVertices.length,
      midpoints: this.cadMidpoints.length,
      centers: this.shapeCenters.length,
      edges: this.cadEdgeSegments.length,
      plane: this.currentSketchPlane
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CAD SHAPE VERTICES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Load vertices and edge midpoints from CAD shapes
   * Call this when shapes are added/removed from the scene
   */
  loadCADShapeVertices(shapes: {
    id: string;
    extractedEdges: { points: number[][] }[];
    extractedFaces?: {
      center: [number, number, number];
      surfaceType?: 'plane' | 'cylinder' | 'cone' | 'sphere' | 'torus' | 'other';
      cylinderData?: { axis: [number, number, number]; location: [number, number, number]; radius: number };
      coneData?: { axis: [number, number, number]; apex: [number, number, number]; semiAngle: number; refRadius: number };
      sphereData?: { center: [number, number, number]; radius: number };
      torusData?: { axis: [number, number, number]; center: [number, number, number]; majorRadius: number; minorRadius: number };
    }[];
    boundingBox?: { min: [number, number, number]; max: [number, number, number] };
  }[]): void {
    this.cadVertices = [];
    this.cadMidpoints = [];
    this.shapeCenters = [];
    this.cadCircleCenters = [];
    this.cadEdgeSegments = [];
    
    const uniqueVertices = new Map<string, { point: Vector3; shapeId: string }>();
    
    for (const shape of shapes) {
      const edges = shape.extractedEdges ?? [];
      const hasEdges = edges.some((edge) => edge.points.length >= 2);

      // Calculate shape center from bounding box or vertices
      let minX = Infinity, minY = Infinity, minZ = Infinity;
      let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
      
      if (shape.boundingBox) {
        // Use provided bounding box
        const center = new Vector3(
          (shape.boundingBox.min[0] + shape.boundingBox.max[0]) / 2,
          (shape.boundingBox.min[1] + shape.boundingBox.max[1]) / 2,
          (shape.boundingBox.min[2] + shape.boundingBox.max[2]) / 2
        );
        this.shapeCenters.push({ point: center, shapeId: shape.id });
      }
      
      if (hasEdges) {
        for (const edge of edges) {
          if (edge.points.length < 2) continue;
          
          // Add vertices (endpoints of edges)
          for (const pt of edge.points) {
            const key = `${shape.id}_${pt[0].toFixed(3)}_${pt[1].toFixed(3)}_${pt[2].toFixed(3)}`;
            if (!uniqueVertices.has(key)) {
              uniqueVertices.set(key, {
                point: new Vector3(pt[0], pt[1], pt[2]),
                shapeId: shape.id
              });
            }
            
            // Track bounding box if not provided
            if (!shape.boundingBox) {
              minX = Math.min(minX, pt[0]);
              minY = Math.min(minY, pt[1]);
              minZ = Math.min(minZ, pt[2]);
              maxX = Math.max(maxX, pt[0]);
              maxY = Math.max(maxY, pt[1]);
              maxZ = Math.max(maxZ, pt[2]);
            }
          }
          
          // Add midpoint of each edge segment
          for (let i = 0; i < edge.points.length - 1; i++) {
            const p1 = edge.points[i];
            const p2 = edge.points[i + 1];
            this.cadEdgeSegments.push({
              p1: new Vector3(p1[0], p1[1], p1[2]),
              p2: new Vector3(p2[0], p2[1], p2[2]),
              shapeId: shape.id
            });
            const midpoint = new Vector3(
              (p1[0] + p2[0]) / 2,
              (p1[1] + p2[1]) / 2,
              (p1[2] + p2[2]) / 2
            );
            this.cadMidpoints.push({ point: midpoint, shapeId: shape.id });
          }
        }
        
        // Calculate shape center from computed bounding box
        if (!shape.boundingBox && minX !== Infinity) {
          const center = new Vector3(
            (minX + maxX) / 2,
            (minY + maxY) / 2,
            (minZ + maxZ) / 2
          );
          this.shapeCenters.push({ point: center, shapeId: shape.id });
        }
      }
      
      // Extract circle centers from cylindrical/spherical faces for precise snapping
      // This is more reliable than edge-based circle detection for holes and bosses
      if (shape.extractedFaces) {
        for (const face of shape.extractedFaces) {
          if (face.surfaceType === 'cylinder' && face.cylinderData) {
            // For cylindrical faces (holes, bosses), the axis location is the circle center
            const { location, radius } = face.cylinderData;
            this.cadCircleCenters.push({
              point: new Vector3(location[0], location[1], location[2]),
              shapeId: shape.id,
              radius: radius,
              label: `Cylinder Center (R=${radius.toFixed(1)})`
            });
          } else if (face.surfaceType === 'sphere' && face.sphereData) {
            // Sphere center
            const { center, radius } = face.sphereData;
            this.cadCircleCenters.push({
              point: new Vector3(center[0], center[1], center[2]),
              shapeId: shape.id,
              radius: radius,
              label: `Sphere Center (R=${radius.toFixed(1)})`
            });
          } else if (face.surfaceType === 'cone' && face.coneData) {
            // Cone apex point
            const { apex, refRadius } = face.coneData;
            this.cadCircleCenters.push({
              point: new Vector3(apex[0], apex[1], apex[2]),
              shapeId: shape.id,
              radius: refRadius,
              label: `Cone Apex`
            });
          } else if (face.surfaceType === 'torus' && face.torusData) {
            // Torus center
            const { center, majorRadius } = face.torusData;
            this.cadCircleCenters.push({
              point: new Vector3(center[0], center[1], center[2]),
              shapeId: shape.id,
              radius: majorRadius,
              label: `Torus Center (R=${majorRadius.toFixed(1)})`
            });
          }
        }
      }

      // Also add the EDGE midpoints (middle of each full edge, not just segments)
      if (hasEdges) {
        for (const edge of edges) {
          if (edge.points.length >= 2) {
            // Calculate the true midpoint of the entire edge
            const firstPt = edge.points[0];
            const lastPt = edge.points[edge.points.length - 1];
            const edgeMidpoint = new Vector3(
              (firstPt[0] + lastPt[0]) / 2,
              (firstPt[1] + lastPt[1]) / 2,
              (firstPt[2] + lastPt[2]) / 2
            );
            // Add as midpoint with a special marker
            this.cadMidpoints.push({ point: edgeMidpoint, shapeId: shape.id });
          }
          
          // Detect circles/arcs from edges
          if (edge.points.length >= 3) {
            const edgePoints = edge.points.map(p => new Vector3(p[0], p[1], p[2]));
            
            // Split the edge into segments if it contains multiple loops
            const segments = this.splitWireIntoSegments(edgePoints);
            
            for (const segment of segments) {
              if (segment.length < 3) continue;
              
              const circleResult = this.detectCircle(segment);
              if (circleResult) {
                this.cadCircleCenters.push({
                  point: circleResult.center,
                  shapeId: shape.id,
                  radius: circleResult.radius,
                  label: `Circle Center (R=${circleResult.radius.toFixed(1)})`
                });
                
                // Add quadrant points for detected circles
                const normal = this.getPlaneNormal(segment);
                if (normal) {
                  const up = new Vector3(0, 1, 0);
                  if (Math.abs(normal.dot(up)) > 0.99) up.set(1, 0, 0);
                  const xAxis = new Vector3().crossVectors(up, normal).normalize();
                  const yAxis = new Vector3().crossVectors(normal, xAxis).normalize();
                  
                  const quadrants = [
                    xAxis.clone().multiplyScalar(circleResult.radius),
                    yAxis.clone().multiplyScalar(circleResult.radius),
                    xAxis.clone().multiplyScalar(-circleResult.radius),
                    yAxis.clone().multiplyScalar(-circleResult.radius)
                  ];
                  
                  for (const offset of quadrants) {
                    const qPt = circleResult.center.clone().add(offset);
                    const key = `${shape.id}_${qPt.x.toFixed(3)}_${qPt.y.toFixed(3)}_${qPt.z.toFixed(3)}`;
                    if (!uniqueVertices.has(key)) {
                      uniqueVertices.set(key, { point: qPt, shapeId: shape.id });
                    }
                  }
                }
              }
            }
          }
        }
      }

      if (!hasEdges && shape.boundingBox) {
        this.addBoundingBoxFallback(shape.id, shape.boundingBox, uniqueVertices);
      }
    }
    
    this.cadVertices = Array.from(uniqueVertices.values());
    
    // Deduplicate circle centers
    this.cadCircleCenters = this.deduplicateCircleCenters(this.cadCircleCenters);
    
    
    
    // Log detected circles for debugging
    for (const circle of this.cadCircleCenters) {
      
    }
  }

  /**
   * Clear CAD shape vertices
   */
  clearCADVertices(): void {
    this.cadVertices = [];
    this.cadMidpoints = [];
    this.shapeCenters = [];
    this.cadCircleCenters = [];
    this.cadEdgeSegments = [];
  }

  private addBoundingBoxFallback(
    shapeId: string,
    boundingBox: { min: [number, number, number]; max: [number, number, number] },
    uniqueVertices: Map<string, { point: Vector3; shapeId: string }>
  ): void {
    const [minX, minY, minZ] = boundingBox.min;
    const [maxX, maxY, maxZ] = boundingBox.max;

    const corners: [number, number, number][] = [
      [minX, minY, minZ],
      [maxX, minY, minZ],
      [minX, maxY, minZ],
      [maxX, maxY, minZ],
      [minX, minY, maxZ],
      [maxX, minY, maxZ],
      [minX, maxY, maxZ],
      [maxX, maxY, maxZ]
    ];

    for (const corner of corners) {
      const key = `${shapeId}_${corner[0].toFixed(3)}_${corner[1].toFixed(3)}_${corner[2].toFixed(3)}`;
      if (!uniqueVertices.has(key)) {
        uniqueVertices.set(key, {
          point: new Vector3(corner[0], corner[1], corner[2]),
          shapeId
        });
      }
    }

    const edges: [number, number][] = [
      [0, 1], [1, 3], [3, 2], [2, 0],
      [4, 5], [5, 7], [7, 6], [6, 4],
      [0, 4], [1, 5], [2, 6], [3, 7]
    ];

    for (const [startIndex, endIndex] of edges) {
      const start = corners[startIndex];
      const end = corners[endIndex];
      const p1 = new Vector3(start[0], start[1], start[2]);
      const p2 = new Vector3(end[0], end[1], end[2]);
      this.cadEdgeSegments.push({ p1, p2, shapeId });

      const midpoint = new Vector3(
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2,
        (start[2] + end[2]) / 2
      );
      this.cadMidpoints.push({ point: midpoint, shapeId });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SKETCH ENTITY SNAP POINTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Load snap points from sketch entities
   * Call this when entities are added/removed from the active sketch
   */
  loadSketchEntitySnapPoints(entities: Array<{
    id: string;
    type: string;
    points: [number, number][];  // 2D points in local sketch coordinates
    center?: { x: number; y: number };
    radius?: number;
  }>, planeOffset: number = 0): void {
    this.sketchVertices = [];
    this.sketchMidpoints = [];
    this.sketchCenters = [];
    this.sketchEdges = [];
    
    for (const entity of entities) {
      const entityId = entity.id;
      const points = entity.points;
      
      if (points.length === 0) continue;
      
      // Convert 2D sketch points to 3D based on current plane
      const to3D = (p: [number, number]): Vector3 => {
        switch (this.currentSketchPlane) {
          case 'XY': return new Vector3(p[0], p[1], planeOffset);
          case 'XZ': return new Vector3(p[0], planeOffset, p[1]);
          case 'YZ': return new Vector3(planeOffset, p[1], p[0]);
          case 'custom':
            // Convert using custom plane axes
            return new Vector3()
              .copy(this.customPlaneOrigin)
              .add(this.customPlaneXAxis.clone().multiplyScalar(p[0]))
              .add(this.customPlaneYAxis.clone().multiplyScalar(p[1]));
          default: return new Vector3(p[0], p[1], planeOffset);
        }
      };
      
      // Add vertices (all points of the entity)
      for (let i = 0; i < points.length; i++) {
        this.sketchVertices.push({
          point: to3D(points[i]),
          entityId,
          label: `Vertex ${i + 1}`
        });
      }
      
      // Add midpoints for edges
      if (points.length >= 2) {
        for (let i = 0; i < points.length - 1; i++) {
          const p1 = points[i];
          const p2 = points[i + 1];
          const midX = (p1[0] + p2[0]) / 2;
          const midY = (p1[1] + p2[1]) / 2;
          this.sketchMidpoints.push({
            point: to3D([midX, midY]),
            entityId,
            label: 'Midpoint'
          });
        }
        
        // For closed shapes, add midpoint of closing edge
        if (entity.type === 'rectangle' || entity.type === 'polygon' || entity.type === 'circle') {
          const p1 = points[points.length - 1];
          const p2 = points[0];
          const midX = (p1[0] + p2[0]) / 2;
          const midY = (p1[1] + p2[1]) / 2;
          this.sketchMidpoints.push({
            point: to3D([midX, midY]),
            entityId,
            label: 'Midpoint'
          });
        }
      }
      
      // Add center point for shapes that have one
      if (entity.center) {
        this.sketchCenters.push({
          point: to3D([entity.center.x, entity.center.y]),
          entityId,
          label: entity.type === 'circle' || entity.type === 'arc' ? 'Circle Center' : 'Center'
        });
        
        // Add quadrant points for circles and arcs (top, right, bottom, left)
        if ((entity.type === 'circle' || entity.type === 'arc') && entity.radius) {
          const cx = entity.center.x;
          const cy = entity.center.y;
          const r = entity.radius;
          
          // Quadrant points (0°, 90°, 180°, 270°)
          const quadrants: { point: [number, number]; label: string }[] = [
            { point: [cx + r, cy], label: 'Quadrant (0°)' },      // Right
            { point: [cx, cy + r], label: 'Quadrant (90°)' },     // Top
            { point: [cx - r, cy], label: 'Quadrant (180°)' },    // Left
            { point: [cx, cy - r], label: 'Quadrant (270°)' }     // Bottom
          ];
          
          for (const q of quadrants) {
            this.sketchVertices.push({
              point: to3D(q.point),
              entityId,
              label: q.label
            });
          }
        }
      }
      
      // Add edge segments for edge snapping
      for (let i = 0; i < points.length - 1; i++) {
        this.sketchEdges.push({
          p1: to3D(points[i]),
          p2: to3D(points[i + 1]),
          entityId
        });
      }
      
      // Close shape edges
      if (entity.type === 'rectangle' || entity.type === 'polygon' || entity.type === 'circle') {
        this.sketchEdges.push({
          p1: to3D(points[points.length - 1]),
          p2: to3D(points[0]),
          entityId
        });
      }
    }
    
    
  }

  /**
   * Clear sketch entity snap points
   */
  clearSketchSnapPoints(): void {
    this.sketchVertices = [];
    this.sketchMidpoints = [];
    this.sketchCenters = [];
    this.sketchEdges = [];
  }

  /**
   * Find snap to sketch entity vertices
   */
  private findSketchVertexSnap(position: Vector3, targetZ: number): SnapResult | null {
    let bestSnap: SnapResult | null = null;
    let bestDistance = this.config.vertexSnapDistance * 3;

    for (const vertex of this.sketchVertices) {
      const dist = this.distance2D(position, vertex.point);
      if (dist < bestDistance) {
        bestDistance = dist;
        const snapPoint = this.projectSnapToPlane(vertex.point, targetZ);
        bestSnap = {
          type: 'vertex',
          point: [snapPoint.x, snapPoint.y, snapPoint.z],
          distance: dist,
          sourceId: vertex.entityId,
          label: vertex.label
        };
      }
    }

    return bestSnap;
  }

  /**
   * Find snap to sketch entity midpoints
   */
  private findSketchMidpointSnap(position: Vector3, targetZ: number): SnapResult | null {
    let bestSnap: SnapResult | null = null;
    let bestDistance = this.config.vertexSnapDistance * 4;

    for (const midpoint of this.sketchMidpoints) {
      const dist = this.distance2D(position, midpoint.point);
      if (dist < bestDistance) {
        bestDistance = dist;
        const snapPoint = this.projectSnapToPlane(midpoint.point, targetZ);
        bestSnap = {
          type: 'midpoint',
          point: [snapPoint.x, snapPoint.y, snapPoint.z],
          distance: dist,
          sourceId: midpoint.entityId,
          label: midpoint.label
        };
      }
    }

    return bestSnap;
  }

  /**
   * Find snap to sketch entity centers
   */
  private findSketchCenterSnap(position: Vector3, targetZ: number): SnapResult | null {
    let bestSnap: SnapResult | null = null;
    let bestDistance = this.config.vertexSnapDistance * 5;

    for (const center of this.sketchCenters) {
      const dist = this.distance2D(position, center.point);
      if (dist < bestDistance) {
        bestDistance = dist;
        const snapPoint = this.projectSnapToPlane(center.point, targetZ);
        bestSnap = {
          type: 'center',
          point: [snapPoint.x, snapPoint.y, snapPoint.z],
          distance: dist,
          sourceId: center.entityId,
          label: center.label
        };
      }
    }

    return bestSnap;
  }

  /**
   * Find snap to sketch entity edges
   */
  private findSketchEdgeSnap(position: Vector3, targetZ: number): SnapResult | null {
    let bestSnap: SnapResult | null = null;
    let bestDistance = this.config.edgeSnapDistance;

    for (const edge of this.sketchEdges) {
      const edgeSnap = this.projectPointToSegment2D(position, edge.p1, edge.p2);
      if (edgeSnap && edgeSnap.distance < bestDistance) {
        bestDistance = edgeSnap.distance;
        bestSnap = {
          type: 'edge',
          point: [edgeSnap.point.x, edgeSnap.point.y, targetZ],
          distance: edgeSnap.distance,
          sourceId: edge.entityId,
          label: 'Edge'
        };
      }
    }

    return bestSnap;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SNAP DETECTION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Find the best snap point for given position
   * Returns null if no snap found within thresholds
   */
  findSnapPoint(position: Vector3, z?: number): SnapResult | null {
    if (!this.config.enabled) {
      return null;
    }

    const candidates: SnapResult[] = [];
    const targetZ = z ?? position.z;

    // 1. Check sketch entity vertex snaps (highest priority for sketching)
    const sketchVertexSnap = this.findSketchVertexSnap(position, targetZ);
    if (sketchVertexSnap) {
      candidates.push(sketchVertexSnap);
    }

    // 2. Check sketch entity center snaps
    const sketchCenterSnap = this.findSketchCenterSnap(position, targetZ);
    if (sketchCenterSnap) {
      candidates.push(sketchCenterSnap);
    }

    // 3. Check sketch entity midpoint snaps
    const sketchMidpointSnap = this.findSketchMidpointSnap(position, targetZ);
    if (sketchMidpointSnap) {
      candidates.push(sketchMidpointSnap);
    }

    // 4. Check sketch entity edge snaps
    const sketchEdgeSnap = this.findSketchEdgeSnap(position, targetZ);
    if (sketchEdgeSnap) {
      candidates.push(sketchEdgeSnap);
    }

    // 5. Check CAD vertex snaps
    const vertexSnap = this.findCADVertexSnap(position, targetZ);
    if (vertexSnap) {
      candidates.push(vertexSnap);
    }

    // 6. Check center snaps (shape and face centers)
    const centerSnap = this.findCenterSnap(position, targetZ);
    if (centerSnap) {
      candidates.push(centerSnap);
    }

    // 6b. Check CAD circle centers (from detected circular edges)
    const cadCircleSnap = this.findCADCircleCenterSnap(position, targetZ);
    if (cadCircleSnap) {
      candidates.push(cadCircleSnap);
    }

    // 7. Check CAD midpoint snaps
    const midpointSnap = this.findCADMidpointSnap(position, targetZ);
    if (midpointSnap) {
      candidates.push(midpointSnap);
    }

    // 8. Check CAD edge snaps
    const edgeSnap = this.findEdgeSnap(position, targetZ);
    if (edgeSnap) {
      candidates.push(edgeSnap);
    }

    // 9. Check horizontal/vertical constraints
    if (this.lastPoint) {
      const hvSnap = this.findHVConstraintSnap(position, this.lastPoint);
      if (hvSnap) {
        candidates.push(hvSnap);
      }
      
      // 5b. Check parallel/perpendicular constraints
      if (this.referenceDirection && this.constraintMode !== 'none') {
        const constraintSnap = this.findConstrainedPoint(position, this.lastPoint, targetZ);
        if (constraintSnap) {
          // Override type with proper parallel/perpendicular type
          constraintSnap.type = this.constraintMode === 'parallel' ? 'parallel' : 'perpendicular';
          candidates.push(constraintSnap);
        }
      }
    }

    // 6. Check grid snap (lowest priority)
    const gridSnap = this.findGridSnap(position, targetZ);
    if (gridSnap) {
      candidates.push(gridSnap);
    }

    // Return the snap with smallest distance (best match)
    if (candidates.length === 0) {
      this.onSnapFound.emit(null);
      return null;
    }

    // Priority weights - higher priority types get a bonus (lower score is better)
    // Centers are now prioritized higher (lower penalty) to catch circle/rect centers better
    const priorityBonus: Record<string, number> = {
      center: 0,       // No penalty - centers are high priority for CAD work
      vertex: 0.2,     // Small penalty (but still high priority)
      midpoint: 0.5,   // Medium-small penalty
      edge: 1.5,       // Medium penalty
      parallel: 2,
      perpendicular: 2,
      horizontal: 2.5,
      vertical: 2.5,
      grid: 4          // Larger penalty for grid snaps
    };

    // Sort by effective distance (distance + priority penalty)
    // This way, a close center will beat a far vertex, but a vertex wins when distances are similar
    candidates.sort((a, b) => {
      const scoreA = a.distance + (priorityBonus[a.type] || 0);
      const scoreB = b.distance + (priorityBonus[b.type] || 0);
      return scoreA - scoreB;
    });
    
    // Debug: show all candidates
    if (candidates.length > 1) {
      
    }

    const bestSnap = candidates[0];
    this.onSnapFound.emit(bestSnap);
    return bestSnap;
  }

  /**
   * Find snap to CAD shape vertices
   */
  private findCADVertexSnap(position: Vector3, targetZ: number): SnapResult | null {
    let bestSnap: SnapResult | null = null;
    let bestDistance = this.config.vertexSnapDistance * 3; // Increase threshold for better snapping

    for (const vertex of this.cadVertices) {
      const dist = this.distance2D(position, vertex.point);
      if (dist < bestDistance) {
        bestDistance = dist;
        // Return snap point projected onto the sketch plane
        const snapPoint = this.projectSnapToPlane(vertex.point, targetZ);
        bestSnap = {
          type: 'vertex',
          point: [snapPoint.x, snapPoint.y, snapPoint.z],
          distance: dist,
          sourceId: vertex.shapeId
        };
      }
    }

    if (bestSnap) {
      
    }

    return bestSnap;
  }

  /**
   * Find snap to CAD edge midpoints
   */
  private findCADMidpointSnap(position: Vector3, targetZ: number): SnapResult | null {
    let bestSnap: SnapResult | null = null;
    let bestDistance = this.config.vertexSnapDistance * 4; // Increase threshold

    for (const midpoint of this.cadMidpoints) {
      const dist = this.distance2D(position, midpoint.point);
      if (dist < bestDistance) {
        bestDistance = dist;
        // Return snap point projected onto the sketch plane
        const snapPoint = this.projectSnapToPlane(midpoint.point, targetZ);
        bestSnap = {
          type: 'midpoint',
          point: [snapPoint.x, snapPoint.y, snapPoint.z],
          distance: dist,
          sourceId: midpoint.shapeId
        };
      }
    }

    if (bestSnap) {
      
    }

    return bestSnap;
  }

  /**
   * Find snap to center points (shape bounding box centers only)
   * Note: We only use shape centers, not face centers, because face centers
   * (vertex centroids) are unreliable for non-planar faces like cylinders
   */
  private findCenterSnap(position: Vector3, targetZ: number): SnapResult | null {
    let bestSnap: SnapResult | null = null;
    let bestDistance = this.config.vertexSnapDistance * 5; // Larger threshold for centers

    // Check shape centers (bounding box centers)
    for (const center of this.shapeCenters) {
      const dist = this.distance2D(position, center.point);
      if (dist < bestDistance) {
        bestDistance = dist;
        const snapPoint = this.projectSnapToPlane(center.point, targetZ);
        bestSnap = {
          type: 'center',
          point: [snapPoint.x, snapPoint.y, snapPoint.z],
          distance: dist,
          sourceId: center.shapeId
        };
      }
    }

    return bestSnap;
  }

  /**
   * Find snap to CAD circle centers (from detected circular edges)
   */
  private findCADCircleCenterSnap(position: Vector3, targetZ: number): SnapResult | null {
    let bestSnap: SnapResult | null = null;
    let bestDistance = this.config.vertexSnapDistance * 8; // Large threshold for circle centers

    for (const circle of this.cadCircleCenters) {
      const dist = this.distance2D(position, circle.point);
      if (dist < bestDistance) {
        bestDistance = dist;
        const snapPoint = this.projectSnapToPlane(circle.point, targetZ);
        bestSnap = {
          type: 'center',
          point: [snapPoint.x, snapPoint.y, snapPoint.z],
          distance: dist,
          sourceId: circle.shapeId,
          label: circle.label
        };
      }
    }

    return bestSnap;
  }

  /**
   * Project a 3D point onto the sketch plane at the given Z offset
   */
  private projectSnapToPlane(point: Vector3, targetZ: number): Vector3 {
    switch (this.currentSketchPlane) {
      case 'XY':
        return new Vector3(point.x, point.y, targetZ);
      case 'XZ':
        return new Vector3(point.x, targetZ, point.z);
      case 'YZ':
        return new Vector3(targetZ, point.y, point.z);
      case 'custom':
        // Project onto custom plane
        return this.projectToCustomPlane(point);
      default:
        return new Vector3(point.x, point.y, targetZ);
    }
  }

  /**
   * Find snap to CAD edges
   */
  private findEdgeSnap(position: Vector3, targetZ: number): SnapResult | null {
    let bestSnap: SnapResult | null = null;
    let bestDistance = this.config.edgeSnapDistance;

    for (const edge of this.cadEdgeSegments) {
      const edgeSnap = this.projectPointToSegment2D(position, edge.p1, edge.p2);
      if (edgeSnap && edgeSnap.distance < bestDistance) {
        bestDistance = edgeSnap.distance;
        bestSnap = {
          type: 'edge',
          point: [edgeSnap.point.x, edgeSnap.point.y, targetZ],
          distance: edgeSnap.distance,
          sourceId: edge.shapeId,
          label: 'Edge'
        };
      }
    }

    return bestSnap;
  }

  /**
   * Find horizontal/vertical constraint snap
   */
  private findHVConstraintSnap(position: Vector3, reference: Vector3): SnapResult | null {
    const pos2d = this.get2DCoords(position);
    const ref2d = this.get2DCoords(reference);
    const du = Math.abs(pos2d.u - ref2d.u);
    const dv = Math.abs(pos2d.v - ref2d.v);
    const threshold = 5; // Pixels/units threshold for H/V snap

    // Check vertical (same X)
    if (this.config.enableVertical && du < threshold && du < dv) {
      const snappedPoint = this.coordsToWorldOnPlane(ref2d.u, pos2d.v, position);
      return {
        type: 'vertical',
        point: [snappedPoint.x, snappedPoint.y, snappedPoint.z],
        distance: du
      };
    }

    // Check horizontal (same Y)
    if (this.config.enableHorizontal && dv < threshold && dv < du) {
      const snappedPoint = this.coordsToWorldOnPlane(pos2d.u, ref2d.v, position);
      return {
        type: 'horizontal',
        point: [snappedPoint.x, snappedPoint.y, snappedPoint.z],
        distance: dv
      };
    }

    return null;
  }

  /**
   * Find grid snap on the current sketch plane
   */
  private findGridSnap(position: Vector3, targetZ: number): SnapResult {
    const gridSize = this.config.gridSize;
    let snappedPoint: Vector3;
    
    switch (this.currentSketchPlane) {
      case 'XY':
        snappedPoint = new Vector3(
          Math.round(position.x / gridSize) * gridSize,
          Math.round(position.y / gridSize) * gridSize,
          targetZ
        );
        break;
      case 'XZ':
        snappedPoint = new Vector3(
          Math.round(position.x / gridSize) * gridSize,
          targetZ,
          Math.round(position.z / gridSize) * gridSize
        );
        break;
      case 'YZ':
        snappedPoint = new Vector3(
          targetZ,
          Math.round(position.y / gridSize) * gridSize,
          Math.round(position.z / gridSize) * gridSize
        );
        break;
      case 'custom':
        // For custom plane, get 2D coords, snap, and convert back
        const coords2D = this.get2DCoords(position);
        const snappedU = Math.round(coords2D.u / gridSize) * gridSize;
        const snappedV = Math.round(coords2D.v / gridSize) * gridSize;
        snappedPoint = this.coordsToWorld(snappedU, snappedV);
        break;
      default:
        snappedPoint = new Vector3(
          Math.round(position.x / gridSize) * gridSize,
          Math.round(position.y / gridSize) * gridSize,
          targetZ
        );
    }
    
    const distance = this.distance2D(position, snappedPoint);

    return {
      type: 'grid',
      point: [snappedPoint.x, snappedPoint.y, snappedPoint.z],
      distance
    };
  }

  /**
   * Convert 2D plane coordinates back to 3D world coordinates
   */
  private coordsToWorld(u: number, v: number): Vector3 {
    switch (this.currentSketchPlane) {
      case 'XY':
        return new Vector3(u, v, 0);
      case 'XZ':
        return new Vector3(u, 0, v);
      case 'YZ':
        return new Vector3(0, u, v);
      case 'custom':
        // Reconstruct 3D point from UV on custom plane
        return new Vector3()
          .copy(this.customPlaneOrigin)
          .add(this.customPlaneXAxis.clone().multiplyScalar(u))
          .add(this.customPlaneYAxis.clone().multiplyScalar(v));
      default:
        return new Vector3(u, v, 0);
    }
  }

  /**
   * Convert 2D plane coordinates back to 3D world coordinates,
   * preserving the axis perpendicular to the sketch plane.
   */
  private coordsToWorldOnPlane(u: number, v: number, anchor: Vector3): Vector3 {
    switch (this.currentSketchPlane) {
      case 'XY':
        return new Vector3(u, v, anchor.z);
      case 'XZ':
        return new Vector3(u, anchor.y, v);
      case 'YZ':
        return new Vector3(anchor.x, u, v);
      case 'custom':
        return this.coordsToWorld(u, v);
      default:
        return new Vector3(u, v, anchor.z);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSTRAINT REFERENCE POINTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Set the reference point for horizontal/vertical constraints
   * Call this when user clicks to add a point
   */
  setLastPoint(point: Vector3 | null): void {
    this.lastPoint = point ? point.clone() : null;
  }

  /**
   * Get the last reference point
   */
  getLastPoint(): Vector3 | null {
    return this.lastPoint;
  }

  /**
   * Clear the reference point
   */
  clearLastPoint(): void {
    this.lastPoint = null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // GEOMETRIC CONSTRAINTS (Parallel, Perpendicular, Tangent)
  // ═══════════════════════════════════════════════════════════════════════════

  // Reference direction for parallel/perpendicular constraints
  private referenceDirection: Vector3 | null = null;
  private constraintMode: 'none' | 'parallel' | 'perpendicular' = 'none';

  /**
   * Set a reference edge direction for parallel/perpendicular constraints
   * @param startPoint Start point of the reference edge
   * @param endPoint End point of the reference edge
   */
  setReferenceEdge(startPoint: Vector3, endPoint: Vector3): void {
    const dir = new Vector3().subVectors(endPoint, startPoint).normalize();
    this.referenceDirection = dir;
    
  }

  /**
   * Clear the reference edge
   */
  clearReferenceEdge(): void {
    this.referenceDirection = null;
    this.constraintMode = 'none';
  }

  /**
   * Set constraint mode (parallel or perpendicular to reference edge)
   */
  setConstraintMode(mode: 'none' | 'parallel' | 'perpendicular'): void {
    this.constraintMode = mode;
    
  }

  /**
   * Get current constraint mode
   */
  getConstraintMode(): 'none' | 'parallel' | 'perpendicular' {
    return this.constraintMode;
  }

  /**
   * Find constrained point based on current constraint mode
   * Returns a point that maintains parallel or perpendicular relationship
   */
  findConstrainedPoint(
    position: Vector3, 
    startPoint: Vector3,
    targetZ: number
  ): SnapResult | null {
    if (!this.referenceDirection || this.constraintMode === 'none') {
      return null;
    }

    const threshold = this.config.edgeSnapDistance;
    const dir2d = this.get2DVector(this.referenceDirection);
    const dirLength = Math.hypot(dir2d.u, dir2d.v);
    if (dirLength < 0.0001) {
      return null;
    }
    const lineDir = { u: dir2d.u / dirLength, v: dir2d.v / dirLength };
    const pos2d = this.get2DCoords(position);
    const start2d = this.get2DCoords(startPoint);
    const toPos = { u: pos2d.u - start2d.u, v: pos2d.v - start2d.v };
    const anchor = this.projectSnapToPlane(position, targetZ);
    
    if (this.constraintMode === 'parallel') {
      // Project onto a line parallel to the reference direction in sketch plane
      const projLength = (toPos.u * lineDir.u) + (toPos.v * lineDir.v);
      const snappedU = start2d.u + lineDir.u * projLength;
      const snappedV = start2d.v + lineDir.v * projLength;
      const snappedPoint = this.coordsToWorldOnPlane(snappedU, snappedV, anchor);

      // Check if we're close enough to consider this a snap
      const dist = this.distance2D(position, snappedPoint);
      
      if (dist < threshold * 2) {
        return {
          type: 'horizontal', // Reuse 'horizontal' type for parallel
          point: [snappedPoint.x, snappedPoint.y, snappedPoint.z],
          distance: dist
        };
      }
    } else if (this.constraintMode === 'perpendicular') {
      // Project onto a line perpendicular to the reference direction in sketch plane
      const perpDir = { u: -lineDir.v, v: lineDir.u };
      const projLength = (toPos.u * perpDir.u) + (toPos.v * perpDir.v);
      const snappedU = start2d.u + perpDir.u * projLength;
      const snappedV = start2d.v + perpDir.v * projLength;
      const snappedPoint = this.coordsToWorldOnPlane(snappedU, snappedV, anchor);

      // Check if we're close enough to consider this a snap
      const dist = this.distance2D(position, snappedPoint);
      
      if (dist < threshold * 2) {
        return {
          type: 'vertical', // Reuse 'vertical' type for perpendicular
          point: [snappedPoint.x, snappedPoint.y, snappedPoint.z],
          distance: dist
        };
      }
    }

    return null;
  }

  /**
   * Get current reference direction for constraints
   */
  getReferenceDirection(): Vector3 | null {
    return this.referenceDirection?.clone() ?? null;
  }

  /**
   * Find the nearest edge from CAD shapes
   * Returns the edge with start and end points
   */
  findNearestEdge(position: Vector3): { start: [number, number, number]; end: [number, number, number]; distance: number } | null {
    let nearest: { start: [number, number, number]; end: [number, number, number]; distance: number } | null = null;

    for (const edge of this.cadEdgeSegments) {
      const proj = this.projectPointToSegment2D(position, edge.p1, edge.p2);
      if (proj && (!nearest || proj.distance < nearest.distance)) {
        nearest = {
          start: [edge.p1.x, edge.p1.y, edge.p1.z],
          end: [edge.p2.x, edge.p2.y, edge.p2.z],
          distance: proj.distance
        };
      }
    }

    return nearest;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // GEOMETRY UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Calculate 2D distance between two points (ignoring Z)
   */
  /**
   * Calculate 2D distance between two points on the current sketch plane
   * Ignores the axis perpendicular to the sketch plane
   */
  private distance2D(p1: Vector3, p2: Vector3): number {
    switch (this.currentSketchPlane) {
      case 'XY':
        // Ignore Z
        return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
      case 'XZ':
        // Ignore Y
        return Math.sqrt((p1.x - p2.x) ** 2 + (p1.z - p2.z) ** 2);
      case 'YZ':
        // Ignore X
        return Math.sqrt((p1.y - p2.y) ** 2 + (p1.z - p2.z) ** 2);
      case 'custom':
        // Project both points onto the plane and calculate distance
        const proj1 = this.projectToCustomPlane(p1);
        const proj2 = this.projectToCustomPlane(p2);
        return proj1.distanceTo(proj2);
      default:
        return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
    }
  }

  /**
   * Project a 3D point onto the custom plane
   */
  private projectToCustomPlane(point: Vector3): Vector3 {
    const toPoint = new Vector3().subVectors(point, this.customPlaneOrigin);
    const dist = toPoint.dot(this.customPlaneNormal);
    return new Vector3().copy(point).sub(this.customPlaneNormal.clone().multiplyScalar(dist));
  }

  /**
   * Get the 2D coordinates on the current sketch plane
   */
  private get2DCoords(p: Vector3): { u: number; v: number } {
    switch (this.currentSketchPlane) {
      case 'XY':
        return { u: p.x, v: p.y };
      case 'XZ':
        return { u: p.x, v: p.z };
      case 'YZ':
        return { u: p.y, v: p.z };
      case 'custom':
        // For custom plane, we need to compute UV coordinates
        const projected = this.projectToCustomPlane(p);
        const toPoint = new Vector3().subVectors(projected, this.customPlaneOrigin);
        return {
          u: toPoint.dot(this.customPlaneXAxis),
          v: toPoint.dot(this.customPlaneYAxis)
        };
      default:
        return { u: p.x, v: p.y };
    }
  }

  private get2DVector(v: Vector3): { u: number; v: number } {
    switch (this.currentSketchPlane) {
      case 'XY':
        return { u: v.x, v: v.y };
      case 'XZ':
        return { u: v.x, v: v.z };
      case 'YZ':
        return { u: v.y, v: v.z };
      case 'custom':
        return { u: v.dot(this.customPlaneXAxis), v: v.dot(this.customPlaneYAxis) };
      default:
        return { u: v.x, v: v.y };
    }
  }

  /**
   * Project a point onto a line segment on the current sketch plane
   * Returns the closest point on the segment and distance, or null if outside segment
   */
  private projectPointToSegment2D(
    point: Vector3,
    segStart: Vector3,
    segEnd: Vector3
  ): { point: Vector3; distance: number } | null {
    // Get 2D coordinates based on current sketch plane
    const p = this.get2DCoords(point);
    const s1 = this.get2DCoords(segStart);
    const s2 = this.get2DCoords(segEnd);
    
    const du = s2.u - s1.u;
    const dv = s2.v - s1.v;
    const lenSquared = du * du + dv * dv;

    if (lenSquared === 0) {
      // Segment is a point
      return null;
    }

    // Calculate projection parameter t
    const t = Math.max(0, Math.min(1, (
      (p.u - s1.u) * du +
      (p.v - s1.v) * dv
    ) / lenSquared));

    // Calculate projected point in 3D
    const projPoint = new Vector3().lerpVectors(segStart, segEnd, t);

    // Calculate distance on the sketch plane
    const distance = this.distance2D(point, projPoint);

    return { point: projPoint, distance };
  }
}
